/**
 * useSessions hook - Manage chat sessions.
 *
 * Provides functionality to:
 * - List user sessions
 * - Create new sessions
 * - Load session details with messages
 * - Update and delete sessions
 * - Generate session metadata
 */

import { useState, useCallback } from 'react';
import type { Session, Message } from '@pdm/axel-ui-components';
import { useAuth } from '../contexts/AuthContext';

interface SessionWithMessages {
  session: Session;
  messages: Message[];
}

interface UseSessionsReturn {
  sessions: Session[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  listSessions: () => Promise<void>;
  loadMore: () => Promise<void>;
  createSession: (title?: string) => Promise<Session>;
  getSession: (sessionID: string) => Promise<SessionWithMessages>;
  updateSession: (
    sessionID: string,
    data: { title?: string; description?: string }
  ) => Promise<void>;
  updateSessionLocal: (sessionID: string, data: { title?: string; description?: string }) => void;
  deleteSession: (sessionID: string) => Promise<void>;
  generateSessionMetadata: (sessionID: string, firstMessage: string) => Promise<void>;
}

export function useSessions(): UseSessionsReturn {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const { apiClient } = useAuth();

  const getClient = useCallback(() => {
    if (!apiClient) {
      throw new Error('Not authenticated');
    }
    return apiClient;
  }, [apiClient]);

  const listSessions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const client = getClient();
      const response = await client.listSessions();

      setSessions(response.sessions);
      setNextCursor(response.nextPageToken);
      setHasMore(Boolean(response.nextPageToken));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load sessions';
      setError(message);
      console.error('Error listing sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [getClient]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const client = getClient();
      const response = await client.listSessions(nextCursor);

      setSessions(prev => [...prev, ...response.sessions]);
      setNextCursor(response.nextPageToken);
      setHasMore(Boolean(response.nextPageToken));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more sessions';
      setError(message);
      console.error('Error loading more sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [hasMore, nextCursor, loading, getClient]);

  const createSession = useCallback(
    async (title?: string): Promise<Session> => {
      setLoading(true);
      setError(null);

      try {
        const client = getClient();
        const session = await client.createSession({ title: title || 'New Chat' });

        // Add new session to the beginning of the list
        setSessions(prev => [session, ...prev]);

        return session;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create session';
        setError(message);
        console.error('Error creating session:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getClient]
  );

  const getSession = useCallback(
    async (sessionID: string): Promise<SessionWithMessages> => {
      setLoading(true);
      setError(null);

      try {
        const client = getClient();
        const response = await client.getSession(sessionID);

        // response is { messages: Message[] } from the API
        // Build session from what we have in local state
        const existingSession = sessions.find(s => s.sessionId === sessionID);
        const session: Session = existingSession || {
          sessionId: sessionID,
          userId: '',
          title: 'Untitled',
          status: 'active',
          messageCount: response.messages.length,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        return {
          session,
          messages: response.messages,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load session';
        setError(message);
        console.error('Error getting session:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getClient, sessions]
  );

  const updateSession = useCallback(
    async (sessionID: string, data: { title?: string; description?: string }): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const client = getClient();
        await client.updateSession(sessionID, data);

        // Update session in the list with the data we sent
        setSessions(prev =>
          prev.map(s =>
            s.sessionId === sessionID
              ? {
                  ...s,
                  title: data.title ?? s.title,
                  description: data.description ?? s.description,
                  updatedAt: new Date().toISOString(),
                }
              : s
          )
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update session';
        setError(message);
        console.error('Error updating session:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getClient]
  );

  const updateSessionLocal = useCallback(
    (sessionID: string, data: { title?: string; description?: string }): void => {
      // Update session in local state without making an API call
      // This is useful when we already have the updated data from another source
      setSessions(prev =>
        prev.map(s =>
          s.sessionId === sessionID
            ? {
                ...s,
                title: data.title ?? s.title,
                description: data.description ?? s.description,
                updatedAt: new Date().toISOString(),
              }
            : s
        )
      );
    },
    []
  );

  const deleteSession = useCallback(
    async (sessionID: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const client = getClient();
        await client.deleteSession(sessionID);

        // Remove session from the list
        setSessions(prev => prev.filter(s => s.sessionId !== sessionID));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete session';
        setError(message);
        console.error('Error deleting session:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getClient]
  );

  const generateSessionMetadata = useCallback(
    async (sessionID: string, firstMessage: string): Promise<void> => {
      try {
        const client = getClient();
        const metadata = await client.generateSessionMetadata(sessionID, firstMessage);

        // Update session in the list with generated metadata
        setSessions(prev =>
          prev.map(s =>
            s.sessionId === sessionID
              ? { ...s, title: metadata.title, description: metadata.description }
              : s
          )
        );
      } catch (err) {
        console.error('Error generating session metadata:', err);
        // Don't throw - this is not critical
      }
    },
    [getClient]
  );

  return {
    sessions,
    loading,
    error,
    hasMore,
    listSessions,
    loadMore,
    createSession,
    getSession,
    updateSession,
    updateSessionLocal,
    deleteSession,
    generateSessionMetadata,
  };
}
